import { DEFAULT_BANNER_FONTS } from './bannerFormats'
import { LINE_HEIGHTS } from './lineHeight'

/**
 * Vocabulários do editor de texto rico.
 *
 * **`toolbar` é um token para o vocabulário do editor**, não um array de botões — a mesma ideia
 * do `size` do `FormModal` e do `variant` do `SectionCard`. Havia 10 definições locais de
 * `toolbar` em quatro vocabulários, e três delas (`eventFormShared`, `series/ProfessionalsEditor`
 * e `Events/FaqEditor`) eram byte a byte iguais, `formats` incluído.
 *
 * `formats` sai daqui junto com a barra, e é isso que impede a divergência que os dois editores
 * do `PromoBannerModal` tinham: sem `formats`, o Quill aceita **toda** a marcação colada da
 * origem, então a barra mostrava três botões e o campo guardava tabelas e imagens inteiras.
 */
export type RichTextToolbar = 'basic' | 'comment' | 'full' | 'color' | 'post' | 'banner'

export interface IRichTextToolbarConfig {
  /** Vai para `modules` do ReactQuill. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  modules: Record<string, any>
  /** O que o editor aceita — inclusive ao colar. */
  formats: string[]
}

/** Título, negrito, lista e link: o vocabulário de 11 dos 19 editores do painel. */
const BASIC = {
  modules: {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
      ['clean']
    ]
  },
  formats: ['header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'link']
}

/** Sem título e sem imagem: comentário de chamado, aviso curto dentro de um formulário. */
const COMMENT = {
  modules: {
    toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link'], ['clean']]
  },
  formats: ['bold', 'italic', 'underline', 'list', 'bullet', 'link']
}

/** Os 13 grupos do regulamento de concurso — o texto longo que é o documento em si. */
const FULL_TOOLBAR = [
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  ['bold', 'italic', 'underline', 'strike'],
  ['blockquote', 'code-block'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ script: 'sub' }, { script: 'super' }],
  [{ indent: '-1' }, { indent: '+1' }],
  [{ direction: 'rtl' }],
  [{ size: ['small', false, 'large', 'huge'] }],
  [{ color: [] }, { background: [] }],
  [{ font: [] }],
  [{ align: [] }],
  ['link', 'image', 'video'],
  ['clean']
]

const FULL_FORMATS = [
  'header',
  'font',
  'size',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'bullet',
  'indent',
  'link',
  'image',
  'video',
  'align',
  'color',
  'background',
  'code-block',
  'script',
  'direction'
]

const FULL = { modules: { toolbar: FULL_TOOLBAR }, formats: FULL_FORMATS }

/**
 * Só negrito, itálico, sublinhado e cor — a faixa promocional, que é uma linha de texto sobre um
 * gradiente. O `handler` existe porque "sem cor" ali não é "cor herdada": o banner tem fundo
 * próprio, e o preto precisa ser escrito.
 */
const COLOR = {
  modules: {
    toolbar: {
      container: [['bold', 'italic', 'underline'], [{ color: [] }], ['clean']],
      handlers: {
        // O `'user'` é obrigatório: os handlers nativos do Quill já formatam nessa origem, e o
        // `RichTextField` ignora as mudanças de origem `api` para não gravar a normalização da
        // montagem. Sem ele, escolher uma cor não chegaria ao formulário.
        color: function (
          this: { quill: { format: (name: string, value: string, source: string) => void } },
          value: string | false
        ) {
          this.quill.format('color', value || '#000000', 'user')
        }
      }
    }
  },
  formats: ['bold', 'italic', 'underline', 'color']
}

/**
 * O vocabulário do blog: o `full` mais o `lineheight`, que é um formato que só o post tem (o
 * `Parchment.Attributor.Style` registrado em `PostsFormModal`). Entra aqui como token em vez de
 * ficar como um `modules` solto no arquivo.
 */
const POST = {
  modules: {
    toolbar: [FULL_TOOLBAR[0], [{ lineheight: LINE_HEIGHTS }], ...FULL_TOOLBAR.slice(1)]
  },
  formats: ['lineheight', ...FULL_FORMATS]
}

/** A paleta padrão do Quill, que o `{ color: [] }` usaria — escrita aqui para caber o "Outra cor". */
const QUILL_PALETTE = [
  '#000000', '#e60000', '#ff9900', '#ffff00', '#008a00', '#0066cc', '#9933ff',
  '#ffffff', '#facccc', '#ffebcc', '#ffffcc', '#cce8cc', '#cce0f5', '#ebd6ff',
  '#bbbbbb', '#f06666', '#ffc266', '#ffff66', '#66b966', '#66a3e0', '#c285ff',
  '#888888', '#a10000', '#b26b00', '#b2b200', '#006100', '#0047b2', '#6b24b2',
  '#444444', '#5c0000', '#663d14', '#666600', '#003700', '#002966', '#3d1466'
] // prettier-ignore

/** Valor do item da paleta que abre o seletor de cor do navegador, para qualquer cor. */
export const CUSTOM_COLOR = 'custom'

interface IToolbarHandlerContext {
  quill: {
    format: (name: string, value: string | false, source: string) => void
    getFormat: () => Record<string, unknown>
  }
}

/**
 * Abre o `<input type="color">` do navegador e aplica a cor ao trecho selecionado.
 *
 * O input é criado na hora e sai do DOM depois da escolha. O `format` roda na seleção que o Quill
 * guardou ao abrir a paleta, e com `'user'` pelo mesmo motivo do token `color`.
 */
function pickCustomColor(context: IToolbarHandlerContext) {
  document.querySelectorAll('input[data-rich-text-color-picker]').forEach((stale) => stale.remove())

  const current = context.quill.getFormat().color
  const input = document.createElement('input')
  input.type = 'color'
  input.dataset.richTextColorPicker = ''
  input.value = typeof current === 'string' && /^#[0-9a-f]{6}$/i.test(current) ? current : '#ffffff'
  Object.assign(input.style, { position: 'fixed', top: '50%', left: '50%', opacity: '0', pointerEvents: 'none' })
  input.addEventListener('change', () => {
    context.quill.format('color', input.value, 'user')
    input.remove()
  })

  document.body.appendChild(input)
  input.click()
}

/**
 * O vocabulário dos Banners Home: título, subtítulo e texto do botão, cada trecho com a própria
 * fonte, tamanho e cor.
 *
 * - **Sem cor é "a cor padrão do campo"** (o `title_color` do banner), não preto como no `color`:
 *   aqui o campo tem uma cor própria, escolhida fora do editor, e o trecho só a sobrepõe.
 * - **Fonte é o formato `bannerfont`** (ver `bannerFormats.ts`), e a lista depende das fontes
 *   enviadas pela aba "Fontes" — por isso este token é montado por função.
 * - **Tamanho é o `size` nativo**, em `em`: relativo ao tamanho de cada campo no site, então
 *   "grande" no título e no botão acompanham cada um o seu, também no mobile.
 */
export function bannerToolbar(fontSlugs: string[]): IRichTextToolbarConfig {
  return {
    modules: {
      toolbar: {
        container: [
          [{ bannerfont: [false, ...fontSlugs] }, { size: ['small', false, 'large', 'huge'] }],
          ['bold', 'italic', 'underline'],
          [{ color: [false, ...QUILL_PALETTE, CUSTOM_COLOR] }],
          ['clean']
        ],
        handlers: {
          color: function (this: IToolbarHandlerContext, value: string | false) {
            if (value === CUSTOM_COLOR) {
              pickCustomColor(this)
              return
            }

            this.quill.format('color', value || false, 'user')
          }
        }
      }
    },
    formats: ['bold', 'italic', 'underline', 'color', 'size', 'bannerfont']
  }
}

export const RICH_TEXT_TOOLBARS: Record<RichTextToolbar, IRichTextToolbarConfig> = {
  basic: BASIC,
  comment: COMMENT,
  full: FULL,
  color: COLOR,
  post: POST,
  banner: bannerToolbar(DEFAULT_BANNER_FONTS.map(({ slug }) => slug))
}
